import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import _ from "lodash";
import { AppImage, ImageFns } from "./image-fns";

import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DialogBaseComponent, DialogParams } from "../dialog-base.component";
import { DomainService } from "../domain.service";

export interface FileGalleryDialogParams extends DialogParams {
  newImages: AppImage[];
  oldImages: AppImage[];
  /** callback when an image is deleted */
  onDelete?: (image: AppImage) => void;
  /** callback when an image is added or changed */
  onChange?: (image: AppImage) => void;
  /** enable Save button to commit changes (if false, parent is responsible for saving) */
  enableSave?: boolean;
}

@Component({
  selector: "app-file-gallery-dialog",
  templateUrl: "./file-gallery-dialog.component.html",
  standalone: false,
})
export class FileGalleryDialogComponent
  extends DialogBaseComponent<FileGalleryDialogParams> implements AfterViewInit, OnDestroy {
  @ViewChild('gal_file') filePicker: ElementRef<HTMLInputElement>;
  activeIndex = 0;
  showGallery = true;
  showEdit = false;
  images: AppImage[] = [];
  cropInput?: File;
  canDelete = false;

  constructor(
    public config: DynamicDialogConfig<FileGalleryDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    public sanitizer: DomSanitizer,
  ) {
    config.header = "View Images";
    config.closable = true;
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      width: "80vw",
      height: "80vh",
    });
    this.prepare();
  }

  prepare(): void {
    this.activeIndex = 0;
    const images = ImageFns.convertFileData(this.params.oldImages);
    this.images = images.concat(this.params.newImages);
    this.images.sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) {
        return -1;
      } else if (b.isPrimary && !a.isPrimary) {
        return 1;
      }
      return a.fileName.localeCompare(b.fileName);
    });
    ImageFns.generateUrls(this.images, this.sanitizer);

    this.canDelete = !!this.params.onDelete;
    this.isPageReady = true;
  }

  ngAfterViewInit(): void {
    if (!this.images.length) {
      this.filePicker.nativeElement.click();
    }
  }

  ngOnDestroy(): void {
    ImageFns.clearUrls(this.images);
  }

  deleteImage(image: AppImage): void {
    if (image && this.params.onDelete) {
      this.params.onDelete(image);
      if (image.blobUrl) {
        image.safeUrl = undefined;
        URL.revokeObjectURL(image.blobUrl);
      }
      _.remove(this.images, (im) => im === image);
      if (this.activeIndex >= this.images.length && this.activeIndex > 0) {
        this.activeIndex--;
      }
      this.redraw();
    }
  }

  /** return false if image is a thumbnail, or has a related thumbnail already */
  canMakeThumbnail(image: AppImage): boolean {
    const thumbname = ImageFns.addSuffix(image.fileName, "-thumb");
    return (
      !image.isThumbnail && !this.images.some((i) => i.fileName == thumbname)
    );
  }

  /** Create a new image as a thumbnail of the given image */
  async makeThumbnail(image: AppImage) {
    if (!image.file) {
      return;
    }
    const thumb = await ImageFns.resizeFileToThumbnail(image.file);
    this.addImage(thumb);
  }

  /** Ensure that at most 1 image is set primary */
  primaryChange(item: AppImage) {
    if (this.params.onChange) {
      this.params.onChange(item);
    }
    if (item.isPrimary) {
      // make all other images non-primary, but treat thumbnails separately
      for (const i of this.images) {
        const primary = i == item;
        if (i.isPrimary != primary && i.isThumbnail == item.isThumbnail) {
          i.isPrimary = primary;
          if (this.params.onChange) {
            this.params.onChange(i);
          }
        }
      }
    }
  }

  /** Open imageCropper for active image */
  edit(item: AppImage) {
    this.cropInput = item.file;
    this.showGallery = false;
    this.showEdit = true;
    this.config.header = "Edit Image";
  }

  /** When Ok clicked on cropper, add the cropped file as a new image */
  async cropOk(blob: Blob) {
    if (blob) {
      const image = this.images[this.activeIndex];
      const file = new File([blob], image.fileName, {
        lastModified: image.file?.lastModified,
        type: blob.type,
      });
      const names = this.images.map((i) => i.fileName);
      const newName = ImageFns.getNextName(image.fileName, names);
      const image2 = await ImageFns.resizeFileToStandard(file, newName);
      this.addImage(image2);
    }
    this.cropCancel();
  }

  /** When Cancel clicked on cropper */
  cropCancel() {
    this.showEdit = false;
    this.showGallery = true;
    this.config.header = "View Images";
  }

  close() {
    ImageFns.clearUrls(this.images);
    this.dialogRef.close();
  }

  /** Return the currently selected image */
  save(appImage: AppImage) {
    const el = document.getElementById("gallery-main-image") as HTMLImageElement;
    if (el) {
      appImage.height = el.naturalHeight;
      appImage.width = el.naturalWidth;
    }

    ImageFns.clearUrls(this.images);
    this.dialogRef.close(appImage);
  }

  /** Add to list as a new image */
  private async addImage(thumb: AppImage) {
    const appImage = await ImageFns.makeImageEntity(thumb, this.uow) as AppImage;
    this.params.newImages?.push(appImage);
    ImageFns.generateUrls([appImage], this.sanitizer);
    this.images.splice(this.activeIndex + 1, 0, appImage);
    this.activeIndex++;
    if (this.params.onChange) {
      this.params.onChange(appImage);
    }
    this.redraw();
  }

  /** Add files chosen by the file control to the newImages collection */
  async addFiles(event: Event) {
    const added = await ImageFns.addFiles(
      event,
      this.params.newImages,
      this.domainService.uow
    );
    if (!added || !added.length) { return; }
    ImageFns.generateUrls(added, this.sanitizer);
    this.activeIndex = this.images.length;
    this.images.push(...added);
    this.redraw();
  }

  /** Hide and show the gallery to reset the thumbnail display. */
  private redraw() {
    this.showGallery = false;
    setTimeout(() => {
      this.showGallery = true;
    }, 0);
  }

}
